import styled, { css, DefaultTheme } from "styled-components";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "tertiary-neg"
  | "ghost"
  | "ghost-neg";

export type ButtonSize = "large" | "medium" | "small" | "mobile" | "x-small";

export const ButtonStyle = styled.button<{
  variant: ButtonVariant;
  size: ButtonSize;
  isFill?: boolean;
  noPadding?: boolean;
}>`
  padding: 1.2rem 3rem;
  border-radius: 0.5rem;
  box-sizing: border-box;
  cursor: pointer;

  ${({ theme, size }) => cssButtonSize(size, theme)}
  width: ${({ isFill }) => (isFill ? "100%" : "")};

  display: flex;
  justify-content: center;
  align-items: center;

  ${({ variant, theme }) => cssByButtonVariant(variant, theme)}

  &:disabled {
    cursor: not-allowed;
  }

  ${({ noPadding }) => noPadding && "padding: unset;"}
`;

/**
 * @description 버튼 종류별로 스타일을 적용해주는 함수
 */
function cssByButtonVariant(variant: ButtonVariant, theme: DefaultTheme) {
  const { color } = theme || {};

  switch (variant) {
    case "primary":
      return css`
        background-color: ${color.button.primary.enabled};
        color: ${color.text.white};

        &:hover,
        &:active {
          background-color: ${color.button.primary.hover};
        }

        &:disabled {
          background-color: ${color.grey[300]};
          color: ${color.text.disabled};
        }
      `;
    case "secondary":
      return css`
        background-color: ${color.grey[200]};
        color: ${color.text.secondary};

        &:hover,
        &:active {
          background-color: ${color.grey[300]};
        }

        &:disabled {
          background-color: ${color.grey[300]};
          color: ${color.text.disabled};
        }
      `;
    case "tertiary":
      return css`
        background-color: ${({ theme }) => theme.color.background.white};
        color: ${color.text.secondary};
        border: 1px solid ${color.stroke.dark};

        &:hover {
          color: ${color.text.tertiery};
          border-color: ${color.stroke.light};
        }

        &:active {
          color: ${color.text.secondary};
          border-color: ${color.stroke.light};
        }

        &:disabled {
          color: ${color.text.disabled};
          border-color: ${color.stroke.light};
        }
      `;
    case "tertiary-neg":
      return css`
        border: 1px solid ${color.status.negative.basic};
        background-color: ${color.white};
        color: ${color.status.negative.basic};

        &:hover,
        &:active {
          opacity: 0.7;
        }

        &:disabled {
          color: ${color.grey["400"]};
          border: 1px solid ${color.grey["400"]};
          opacity: 1;
        }
      `;
    case "ghost":
      return css`
        background-color: transparent;
        color: ${color.grey["600"]};

        &:hover,
        &:active {
          color: ${color.grey["500"]};
        }

        &:disabled {
          color: ${color.grey["400"]};
        }
      `;

    case "ghost-neg":
      return css`
        background-color: transparent;
        color: ${color.status.negative.basic};

        &:hover,
        &:active {
          opacity: 0.7;
        }

        &:disabled {
          color: ${color.grey["400"]};
          opacity: 1;
        }
      `;
    default:
      return;
  }
}

/**
 * @description 버튼 사이즈별로 CSS 분기 처리를 해주는 함수
 */
function cssButtonSize(size: ButtonSize, theme: DefaultTheme) {
  switch (size) {
    case "large":
      return css`
        height: 4.8rem;
        padding: 1.2rem 3rem;
        gap: 0.4rem;
        ${theme.typography.body1.semibold}

        svg {
          width: 2rem;
          height: 2rem;
        }
      `;
    case "medium":
      return css`
        height: 4rem;
        padding: 0.8rem 1.6rem;
        gap: 0.2rem;
        ${theme.typography.body2.medium}
        letter-spacing: -0.045rem;

        svg {
          width: 2rem;
          height: 2rem;
        }
      `;
    case "small":
      return css`
        height: 3.2rem;
        padding: 0.6rem 1.6rem;
        gap: 0.2rem;
        ${theme.typography.body3.regular}

        svg {
          width: 1.6rem;
          height: 1.6rem;
        }
      `;
    case "mobile":
      return css`
        padding: 1.3rem 3rem;
        gap: 0.4rem;
        ${theme.typography.body3.semibold}

        svg {
          width: 2em;
          height: 2rem;
        }
      `;
    case "x-small":
      return css`
        height: 2.8rem;
        padding: 0.4rem 1.2rem;
        ${theme.typography.caption1.regular}

        svg {
          width: 1.2rem;
          height: 1.2rem;
        }
      `;
    default:
      return;
  }
}
